import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  CheckCircle, AlertCircle, ArrowLeft, Users,
  Clock, Plus, RefreshCw, X, Fingerprint, Send, Search,
  Wallet, CreditCard,
} from "lucide-react";
import $ from "jquery";
import useApi from "../../../CustomHooks/useApi";
import useRDService from "../../../CustomHooks/useRDService";
import useGeoLocation from "../../../CustomHooks/useGeoLocation";
import DeviceStatus from "../NewAeps/DeviceStatus";
import bankMaster from "../../../MasterJson/bank_master.json";
import Popup from "../../../ResuableCompoment/Popup";

// ── Parse PID XML from RD service ──────────────────────────────────────────
const parsePidXml = (xml) => {
  const $xml = $(xml);
  const get = (sel, attr) => $xml.find(sel).attr(attr) || "0";
  return {
    pidData: $xml.find("Data").text() || "0",
    dc: get("DeviceInfo", "dc"),
    ci: get("Skey", "ci"),
    hmac: $xml.find("Hmac").text() || "0",
    dpId: get("DeviceInfo", "dpId"),
    mc: get("DeviceInfo", "mc"),
    rdsId: get("DeviceInfo", "rdsId"),
    rdsVer: get("DeviceInfo", "rdsVer"),
  };
};

// ── Step config ─────────────────────────────────────────────────────────────
const STEPS = [
  { label: "Verify" },
  { label: "Consent" },
  { label: "KYC" },
  { label: "Beneficiary" },
  { label: "Transfer" },
  { label: "Done" },
];

const STEP_IDX = {
  mobile_entry: 0, otp_verify: 0,
  consent: 1,
  ekyc: 2, pan_verify: 2,
  beneficiary_list: 3, add_beneficiary: 3,
  fund_transfer: 4,
  tx_status: 5, refund: 5,
};

const BACK_MAP = {
  otp_verify: "mobile_entry",
  consent: "mobile_entry",
  ekyc: "mobile_entry",
  pan_verify: "ekyc",
  add_beneficiary: "beneficiary_list",
  fund_transfer: "beneficiary_list",
  refund: "tx_status",
};

// ── Shared UI pieces (matching Electricity style) ───────────────────────────
const NAVY = "#12345A";

const FieldLabel = ({ text, req }) => (
  <label className="text-xl font-medium text-slate-700 block mb-2">
    {text}
    {req && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const BottomInput = ({ className = "", ...props }) => (
  <input
    {...props}
    className={`w-full min-h-[60px] sm:min-h-[70px] px-4 py-3 rounded-xl border border-[#12345A] focus:ring-2 focus:ring-indigo-100 focus:border-indigo-100 disabled:bg-gray-100 transition text-xl sm:text-[22px] text-slate-800 font-bold bg-[#F6F6F6] shadow-sm placeholder-slate-400 ${className}`}
  />
);

const PrimaryBtn = ({ children, disabled, onClick, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-full px-4 py-4 bg-[#12345A] hover:bg-[#0F2E47] text-white rounded-xl font-semibold shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2 text-base ${className}`}
  >
    {children}
  </button>
);

const SecondaryBtn = ({ children, onClick, className = "" }) => (
  <button
    onClick={onClick}
    className={`w-full px-4 py-4 bg-white border-2 border-slate-300 text-slate-600 rounded-xl font-semibold shadow-sm hover:bg-slate-50 transition flex justify-center items-center gap-2 text-base ${className}`}
  >
    {children}
  </button>
);

const ErrBox = ({ msg }) =>
  msg ? (
    <div className="px-4 py-3 rounded-xl bg-red-100 text-red-700 text-sm font-medium mb-5">
      {msg}
    </div>
  ) : null;

const InfoBox = ({ msg }) =>
  msg ? (
    <div className="px-4 py-3 rounded-xl bg-green-50 text-green-800 text-sm font-medium mb-5 border border-green-200">
      {msg}
    </div>
  ) : null;

const SectionTitle = ({ title, subtitle, icon: Icon }) => (
  <div className="flex items-start gap-4 mb-7">
    {Icon && (
      <div className="w-14 h-14 rounded-2xl bg-[#F6F6F6] flex items-center justify-center text-[#12345A] shadow-lg shrink-0">
        <Icon className="w-7 h-7" />
      </div>
    )}
    <div>
      <h2 className="text-2xl font-semibold text-slate-800">{title}</h2>
      {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
    </div>
  </div>
);

// ── Main Component ──────────────────────────────────────────────────────────
export default function NewPPIMain() {
  const mid = useSelector((s) => s.auth.employeeDetails?.username);
  const merchantAccountNo = useSelector((s) => s.auth.employeeDetails?.merchant_account_no);
  const { device, captureFingerprint, loading: rdLoading } = useRDService();
  const { location, fetchLocation } = useGeoLocation();

  useEffect(() => { fetchLocation(); }, []);

  const [screen, setScreen] = useState("mobile_entry");
  const [loading, setLoading] = useState(false);
  const [errorPopup, setErrorPopup] = useState("");

  // Extract the most descriptive error message from API error or response
  const extractApiError = (e, fallback) =>
    e?.response?.data?.errorMessage ||
    e?.response?.data?.responseMessage ||
    e?.response?.data?.message ||
    e?.message ||
    fallback ||
    "Something went wrong. Please try again.";

  const extractResError = (res, fallback) =>
    res?.errorMessage ||
    res?.responseMessage ||
    res?.message ||
    fallback ||
    "Something went wrong. Please try again.";

  // Session
  const [mobile, setMobile] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [adharVerified, setAdharVerified] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [remitterName, setRemitterName] = useState("");

  // OTP verify
  const [otp, setOtp] = useState("");

  // Consent
  const [consentLangs, setConsentLangs] = useState([]);
  const [selectedLang, setSelectedLang] = useState("en");
  const [consentText, setConsentText] = useState("");

  // eKYC
  const [aadhaar, setAadhaar] = useState("");
  const [ekycMode, setEkycMode] = useState("otp");
  const [aadhaarOtpToken, setAadhaarOtpToken] = useState("");
  const [aadhaarOtp, setAadhaarOtp] = useState("");
  const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);

  // PAN
  const [pan, setPan] = useState("");

  // Wallet balances from verify OTP response
  const [availableBalance, setAvailableBalance] = useState(null);
  const [usedBalance, setUsedBalance] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);

  // Beneficiaries
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selectedBene, setSelectedBene] = useState(null);
  const [transferAmt, setTransferAmt] = useState("");

  // Add bene form
  const EMPTY_BENE = { beneficiaryName: "", beneficiaryPhoneNumber: "", bankName: "", bankIfscCode: "", accountNumber: "", mpin: "" };
  const [newBene, setNewBene] = useState({ ...EMPTY_BENE });

  // Fund transfer
  const [paymentMode, setPaymentMode] = useState("IMPS");
  const [mpin, setMpin] = useState("");
  const [txnOtp, setTxnOtp] = useState(["", "", "", "", "", ""]);
  const [txnOtpToken, setTxnOtpToken] = useState("");
  const [txnId, setTxnId] = useState("");
  const [txnResult, setTxnResult] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const txnOtpRefs = useRef([]);

  // Fee calculation
  const [feeData, setFeeData] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  // Add-beneficiary OTP
  const [addBeneOtpSent, setAddBeneOtpSent] = useState(false);
  const [addBeneOtp, setAddBeneOtp] = useState("");

  // Delete-beneficiary OTP
  const [deleteBeneOtpTarget, setDeleteBeneOtpTarget] = useState(null); // { bene_id, bene_acc_no }
  const [deleteBeneOtp, setDeleteBeneOtp] = useState("");

  // Refund
  const [refundOtp, setRefundOtp] = useState("");
  const [refundOtpToken, setRefundOtpToken] = useState("");
  const [refundResult, setRefundResult] = useState(null);

  // Bank search
  const [bankSearch, setBankSearch] = useState("");
  const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
  const bankDropdownRef = useRef(null);

  const filteredBanks = bankSearch.trim().length > 1
    ? bankMaster.filter((b) =>
        b.bank_name.toLowerCase().includes(bankSearch.toLowerCase()) ||
        b.bank_code.toLowerCase().includes(bankSearch.toLowerCase())
      ).slice(0, 20)
    : [];

  useEffect(() => {
    const handler = (e) => {
      if (bankDropdownRef.current && !bankDropdownRef.current.contains(e.target)) {
        setBankDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Auto fee calculation (debounced on amount change) ─────────────────────
  useEffect(() => {
    const amt = parseFloat(transferAmt);
    if (!transferAmt || isNaN(amt) || amt <= 0) { setFeeData(null); return; }
    const timer = setTimeout(async () => {
      setFeeLoading(true);
      try {
        const res = await calculateFeeApi({
          paymentMode,
          provider: "PPI",
          sub_provider: "PPI",
          amount: amt,
          commercial_type: "TRANSACTION_FEE",
          mid,
        });
        setFeeData(res);
      } catch { setFeeData(null); }
      finally { setFeeLoading(false); }
    }, 600);
    return () => clearTimeout(timer);
  // eslint-disable-next-line
  }, [transferAmt]);

  // ── API hooks (all 13 endpoints) ───────────────────────────────────────────
  const { callApi: generateOtpApi }    = useApi("/ppi/otp/generate",       "POST");
  const { callApi: verifyOtpApi }      = useApi("/ppi/otp/verify",          "POST");
  const { callApi: getConsentApi }     = useApi("/ppi/get/consent",         "GET");
  const { callApi: validateConsentApi }= useApi("/ppi/validate/consent",    "POST");
  const { callApi: ekycApi }              = useApi("/ppi/ekyc",                     "POST");
  const { callApi: aadhaarOtpGenerateApi }= useApi("/ppi/aadhaar/otp/generate",    "POST");
  const { callApi: aadhaarOtpResendApi }  = useApi("/ppi/aadhaar/otp/resend",      "POST");
  const { callApi: aadhaarOtpValidateApi }= useApi("/ppi/aadhaar/otp/validate",    "POST");
  const { callApi: verifyPanApi }      = useApi("/ppi/verify/pan",          "POST");
  const { callApi: addBeneApi }        = useApi("/ppi/add/beneficiary",     "POST");
  const { callApi: listBeneApi }       = useApi("/ppi/list/beneficiary",    "GET");
  const { callApi: deleteBeneApi }     = useApi("/ppi/delete/beneficiary",  "PUT");
  const { callApi: transferApi }       = useApi("/ppi/transfer/money",      "POST");
  const { callApi: checkStatusApi }    = useApi("/ppi/check/status",        "POST");
  const { callApi: refundSendOtpApi }  = useApi("/ppi/refund/send/otp",     "POST");
  const { callApi: refundValidateApi }        = useApi("/ppi/refund/validate/otp",              "POST");
  const { callApi: calculateFeeApi }          = useApi("/dmt/fee/calculate",                     "POST");
  const { callApi: beneOtpResendApi }         = useApi("/ppi/beneficiary/otp/resend",            "POST");
  const { callApi: beneOtpValidateApi }       = useApi("/ppi/beneficiary/otp/validate",          "POST");
  const { callApi: deleteBeneOtpGenerateApi } = useApi("/ppi/beneficiary/delete/otp/generate",   "POST");
  const { callApi: deleteBeneOtpValidateApi } = useApi("/ppi/beneficiary/delete/otp/validate",   "POST");

  // ── Helpers ────────────────────────────────────────────────────────────────
  const go = (sc) => { setScreen(sc); setErrorPopup(""); };
  const goBack = () => { const p = BACK_MAP[screen]; if (p) go(p); };

  const afterKyc = (adhar, panV) => {
    if (!adhar) { go("ekyc"); return; }
    if (!panV)  { go("pan_verify"); return; }
    loadBeneficiaries();
    go("beneficiary_list");
  };

  // ── PPI-1 Generate OTP ─────────────────────────────────────────────────────
  const handleGenerateOtp = async () => {
    if (!mobile || mobile.length !== 10) { setErrorPopup("Enter a valid 10-digit mobile number"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await generateOtpApi({ mobile_number: mobile, mid });
      if (res?.status === "SUCCESS" || res?.otp_token) {
        setOtpToken(res.otp_token || ""); setOtp(""); go("otp_verify");
      } else { setErrorPopup(extractResError(res, "Failed to generate OTP")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Failed to generate OTP")); }
    finally { setLoading(false); }
  };

  // ── PPI-2 Verify OTP ───────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) { setErrorPopup("Enter the OTP received on mobile"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await verifyOtpApi(
        { otp_token: otpToken, otp },
        `mobile_number=${mobile}&userName=${mid}&merchant_account_no=${merchantAccountNo || ""}`
      );
      if (res?.token || res?.otp_status === "VERIFIED") {
        const tok = res.token || "";
        const adhar = !!res.adhar_verified;
        const panV = !!res.pan_verified;
        setSessionToken(tok);
        setAdharVerified(adhar);
        setPanVerified(panV);
        if (res.customer_name) setRemitterName(res.customer_name);
        if (res.available_balance != null) setAvailableBalance(res.available_balance);
        if (res.used_balance != null) setUsedBalance(res.used_balance);
        if (res.wallet_balance != null) setWalletBalance(res.wallet_balance);

        if (adhar && panV) {
          // Aadhaar + PAN already verified — skip Consent & KYC, go straight to Beneficiary
          await loadBeneficiaries(tok);
          go("beneficiary_list");
        } else {
          // Load consent languages then go to consent screen
          await loadConsentLangs(tok);
          go("consent");
        }
      } else { setErrorPopup(extractResError(res, "OTP verification failed")); }
    } catch (e) { setErrorPopup(extractApiError(e, "OTP verification failed")); }
    finally { setLoading(false); }
  };

  // ── PPI-3 Get Consent ─────────────────────────────────────────────────────
  const loadConsentLangs = async (tok) => {
    try {
      const res = await getConsentApi(null, `token=${tok || sessionToken}`);
      if (Array.isArray(res)) {
        setConsentLangs(res);
        if (res.length) setSelectedLang(res[0].language_code || "en");
      }
    } catch { /* silent */ }
  };

  // ── PPI-4 Validate Consent ────────────────────────────────────────────────
  const handleAcceptConsent = async () => {
    setLoading(true); setErrorPopup("");
    try {
      const res = await validateConsentApi({ consentlang_id: selectedLang }, `token=${sessionToken}`);
      if (res?.consent === "ACCEPTED" || res?.consent_content || res?.status === "SUCCESS") {
        if (res.consent_content) setConsentText(res.consent_content);
        afterKyc(adharVerified, panVerified);
      } else { setErrorPopup(extractResError(res, "Consent validation failed")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Consent validation failed")); }
    finally { setLoading(false); }
  };

  // ── PPI-5 eKYC ────────────────────────────────────────────────────────────
  const handleEkyc = async () => {
    if (!aadhaar || aadhaar.length !== 12) { setErrorPopup("Enter a valid 12-digit Aadhaar number"); return; }
    if (!device) { setErrorPopup("No biometric device found. Please connect your RD device."); return; }
    setLoading(true); setErrorPopup("");
    try {
      const wadhValue = "TF/lfPuh1n4ZY1xizYpqikIBm+gv65r51MFNek4uwNw="
      const pidXml = await captureFingerprint(wadhValue);
      if (!pidXml) { setErrorPopup("Fingerprint capture failed. Please try again."); setLoading(false); return; }
      const parsed = parsePidXml(pidXml);
      const res = await ekycApi({
        mid, mobile, adhaar: aadhaar,
        lat: location?.lat?.toString() || "0", lng: location?.lng?.toString() || "0",
        consent: 1, consent_taken: true,
        biometric_code: btoa(pidXml),
        ...parsed, token: sessionToken,
      });
      if (res?.adhaarVerified === true || res?.status === "SUCCESS") {
        setAdharVerified(true);
        if (!panVerified) { go("pan_verify"); } else { loadBeneficiaries(); go("beneficiary_list"); }
      } else { setErrorPopup(extractResError(res, "Aadhaar verification failed")); }
    } catch (e) { setErrorPopup(extractApiError(e, "eKYC failed")); }
    finally { setLoading(false); }
  };

  // ── Aadhaar OTP Generate ──────────────────────────────────────────────────
  const handleAadhaarOtpGenerate = async () => {
    if (!aadhaar || aadhaar.length !== 12) { setErrorPopup("Enter a valid 12-digit Aadhaar number"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await aadhaarOtpGenerateApi({ mid, mobile, aadhaar_no: aadhaar, consent_id: "1", token: sessionToken });
      if (res?.status === "Success" || res?.otp_token) {
        setAadhaarOtpToken(res.otp_token || ""); setAadhaarOtp(""); setAadhaarOtpSent(true);
      } else { setErrorPopup(extractResError(res, "Failed to send Aadhaar OTP")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Failed to send Aadhaar OTP")); }
    finally { setLoading(false); }
  };

  // ── Aadhaar OTP Resend ────────────────────────────────────────────────────
  const handleAadhaarOtpResend = async () => {
    setLoading(true); setErrorPopup("");
    try {
      const res = await aadhaarOtpResendApi({ otpToken: aadhaarOtpToken, token: sessionToken });
      if (res?.otp_token) { setAadhaarOtpToken(res.otp_token); setAadhaarOtp(""); }
      else { setErrorPopup(extractResError(res, "Failed to resend Aadhaar OTP")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Failed to resend Aadhaar OTP")); }
    finally { setLoading(false); }
  };

  // ── Aadhaar OTP Validate ──────────────────────────────────────────────────
  const handleAadhaarOtpValidate = async () => {
    if (!aadhaarOtp || aadhaarOtp.length !== 6) { setErrorPopup("Enter the 6-digit Aadhaar OTP"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await aadhaarOtpValidateApi({ mid, mobile, otpToken: aadhaarOtpToken, otp: aadhaarOtp, token: sessionToken });
      if (res?.aadhaarVerified === true || res?.status === "Success") {
        setAdharVerified(true);
        if (!panVerified) { go("pan_verify"); } else { loadBeneficiaries(); go("beneficiary_list"); }
      } else { setErrorPopup(extractResError(res, "Aadhaar OTP verification failed")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Aadhaar OTP verification failed")); }
    finally { setLoading(false); }
  };

  // ── PPI-6 Verify PAN ──────────────────────────────────────────────────────
  const handleVerifyPan = async () => {
    if (!pan || !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan.toUpperCase())) {
      setErrorPopup("Enter a valid 10-character PAN (e.g. ABCDE1234F)"); return;
    }
    setLoading(true); setErrorPopup("");
    try {
      const res = await verifyPanApi({ pan: pan.toUpperCase(), remitter_mobile: mobile, mid, token: sessionToken });
      if (res?.pan_status === true || res?.status === "SUCCESS") {
        setPanVerified(true); loadBeneficiaries(); go("beneficiary_list");
      } else { setErrorPopup(extractResError(res, "PAN verification failed")); }
    } catch (e) { setErrorPopup(extractApiError(e, "PAN verification failed")); }
    finally { setLoading(false); }
  };

  // ── PPI-8 List Beneficiaries ───────────────────────────────────────────────
  const loadBeneficiaries = async (tok) => {
    const tokenToUse = tok || sessionToken;
    if (!tokenToUse) return;
    setLoading(true);
    try {
      const res = await listBeneApi(null, `token=${tokenToUse}&mobile=${mobile}`);
      setBeneficiaries(Array.isArray(res) ? res : []);
    } catch { setBeneficiaries([]); }
    finally { setLoading(false); }
  };

  // ── PPI-14 Delete Beneficiary – Step 1: generate OTP ──────────────────────
  const handleDeleteBene = async (beneId, beneAccNo, e) => {
    e.stopPropagation();
    setErrorPopup("");
    setLoading(true);
    try {
      const res = await deleteBeneOtpGenerateApi({ beneficiary_id: beneId, remitter_mobile: mobile, token: sessionToken });
      if (res?.status === "Success" || res?.otp_token) {
        setDeleteBeneOtpTarget({ bene_id: beneId, bene_acc_no: beneAccNo });
        setDeleteBeneOtp("");
      } else { setErrorPopup(extractResError(res, "Failed to send delete OTP")); }
    } catch (err) { setErrorPopup(extractApiError(err, "Failed to send delete OTP")); }
    finally { setLoading(false); }
  };

  // ── PPI-14 Delete Beneficiary – Step 2: validate OTP ──────────────────────
  const handleDeleteBeneOtpValidate = async () => {
    if (!deleteBeneOtp) { setErrorPopup("Enter the OTP to confirm delete"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await deleteBeneOtpValidateApi({
        beneficiary_id: deleteBeneOtpTarget.bene_id,
        remitter_mobile: mobile,
        otp: deleteBeneOtp,
        token: sessionToken,
      });
      if (res?.status === "Success" || res?.message?.toLowerCase().includes("success")) {
        setDeleteBeneOtpTarget(null); setDeleteBeneOtp(""); await loadBeneficiaries();
      } else { setErrorPopup(extractResError(res, "Delete OTP verification failed")); }
    } catch (err) { setErrorPopup(extractApiError(err, "Delete OTP verification failed")); }
    finally { setLoading(false); }
  };

  // ── PPI-7 Add Beneficiary ─────────────────────────────────────────────────
  const handleAddBene = async () => {
    const { beneficiaryName, bankName, bankIfscCode, accountNumber, mpin: bnMpin } = newBene;
    if (!beneficiaryName || !bankName || !bankIfscCode || !accountNumber || !bnMpin) {
      setErrorPopup("All required fields must be filled"); return;
    }
    setLoading(true); setErrorPopup("");
    try {
      const res = await addBeneApi({
        remitterPhoneNumber: mobile, beneficiaryName: newBene.beneficiaryName,
        beneficiaryPhoneNumber: newBene.beneficiaryPhoneNumber,
        bankName: newBene.bankName, bankIfscCode: newBene.bankIfscCode.toUpperCase(),
        accountNumber: newBene.accountNumber, user_name: mid, merchant_id: mid,
        token: sessionToken, mpin: newBene.mpin, paymentMode: "IMPS",
      });
      if (res?.beneficiary_status === "OTP_SENT" || res?.otp_token) {
        setAddBeneOtpSent(true); setAddBeneOtp("");
      } else if (res?.beneficiary_id || res?.beneficiary_status === "APPROVED" || res?.status === "SUCCESS") {
        setNewBene({ ...EMPTY_BENE }); setAddBeneOtpSent(false); await loadBeneficiaries(); go("beneficiary_list");
      } else { setErrorPopup(extractResError(res, "Failed to add beneficiary")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Failed to add beneficiary")); }
    finally { setLoading(false); }
  };

  // ── Add-Beneficiary OTP: Validate ────────────────────────────────────────
  const handleBeneOtpValidate = async () => {
    if (!addBeneOtp || addBeneOtp.length < 4) { setErrorPopup("Enter the OTP sent to verify the beneficiary"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await beneOtpValidateApi({
        account_number: newBene.accountNumber,
        remitter_mobile: mobile,
        otp: addBeneOtp,
        token: sessionToken,
      });
      if (res?.status === "Success" || res?.message?.toLowerCase().includes("success")) {
        setNewBene({ ...EMPTY_BENE }); setAddBeneOtpSent(false); setAddBeneOtp("");
        await loadBeneficiaries(); go("beneficiary_list");
      } else { setErrorPopup(extractResError(res, "OTP verification failed")); }
    } catch (e) { setErrorPopup(extractApiError(e, "OTP verification failed")); }
    finally { setLoading(false); }
  };

  // ── Add-Beneficiary OTP: Resend ───────────────────────────────────────────
  const handleBeneOtpResend = async () => {
    setLoading(true); setErrorPopup("");
    try {
      await beneOtpResendApi({ account_number: newBene.accountNumber, remitter_mobile: mobile, token: sessionToken });
      setAddBeneOtp("");
    } catch (e) { setErrorPopup(extractApiError(e, "Failed to resend OTP")); }
    finally { setLoading(false); }
  };

  // ── PPI-9 Transfer – Step 1: send OTP ─────────────────────────────────────
  const handleSendTxnOtp = async () => {
    if (!mpin || mpin.length < 4) { setErrorPopup("Enter your MPIN"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await transferApi({
        userName: mid, remitter_mobile_no: mobile,
        beneficiary_account_no: selectedBene.bene_acc_no,
        txn_amount: feeData?.txn_amount ?? parseFloat(transferAmt),
        net_amount: feeData?.net_amount ?? parseFloat(transferAmt),
        payment_mode: paymentMode, merchant_account_no: merchantAccountNo || "",
        token: sessionToken, mpin,
      });
      if (res?.action_params?.token) {
        setTxnOtpToken(res.action_params.token);
        setTxnId(res.action_params.trxnId || "");
        setOtpSent(true);
      } else if (res?.status === "SUCCESS") {
        setTxnResult(res); go("tx_status");
      } else { setErrorPopup(extractResError(res, "Failed to send OTP")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Transfer initiation failed")); }
    finally { setLoading(false); }
  };

  // ── PPI-9 Transfer – Step 2: confirm ──────────────────────────────────────
  const handleConfirmTransfer = async () => {
    const otpStr = txnOtp.join("");
    if (otpStr.length < 4) { setErrorPopup("Enter the OTP received on mobile"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await transferApi({
        userName: mid, remitter_mobile_no: mobile,
        beneficiary_account_no: selectedBene.bene_acc_no,
        txn_amount: feeData?.txn_amount ?? parseFloat(transferAmt),
        net_amount: feeData?.net_amount ?? parseFloat(transferAmt),
        payment_mode: paymentMode, txntype: paymentMode, merchant_account_no: merchantAccountNo || "",
        token: sessionToken, mpin, otp: otpStr, txnId, otptoken: txnOtpToken,
      });
      setTxnResult(res); go("tx_status");
    } catch (e) { setErrorPopup(extractApiError(e, "Transfer failed")); }
    finally { setLoading(false); }
  };

  // ── PPI-11 Check Status ───────────────────────────────────────────────────
  const handleCheckStatus = async () => {
    if (!txnResult?.txnId) return;
    setLoading(true);
    try {
      const res = await checkStatusApi(null, `txn_id=${txnResult.txnId}&token=${sessionToken}`);
      if (res?.status) setTxnResult((p) => ({ ...p, status: res.status }));
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  // ── PPI-12 Refund send OTP ────────────────────────────────────────────────
  const handleRefundSendOtp = async () => {
    setLoading(true); setErrorPopup("");
    try {
      const res = await refundSendOtpApi({
        remitter_mobile: mobile, txn_reference_id: txnResult?.externalRefNumber || txnResult?.txnId, token: sessionToken,
      });
      if (res?.otp_token || res?.status === "SUCCESS") { setRefundOtpToken(res.otp_token || ""); }
      else { setErrorPopup(extractResError(res, "Failed to send refund OTP")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Failed to send refund OTP")); }
    finally { setLoading(false); }
  };

  // ── PPI-13 Refund validate OTP ────────────────────────────────────────────
  const handleRefundValidate = async () => {
    if (!refundOtp) { setErrorPopup("Enter the refund OTP"); return; }
    setLoading(true); setErrorPopup("");
    try {
      const res = await refundValidateApi({
        otp_token: refundOtpToken, otp: refundOtp, mid, token: sessionToken,
        remitterMobile: mobile,
        externalRefNo: txnResult?.externalRefNumber || txnResult?.txnId,
        txnId: txnResult?.txnId,
      });
      if (res?.refundTxnId || res?.status === "SUCCESS") { setRefundResult(res); }
      else { setErrorPopup(extractResError(res, "Refund failed")); }
    } catch (e) { setErrorPopup(extractApiError(e, "Refund failed")); }
    finally { setLoading(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const stepIdx = STEP_IDX[screen] ?? 0;

  return (
    <div className="w-full py-5 px-4 font-sans">
      <div className="w-full">

        {/* ── Step indicator ─────────────────────────────────────── */}
        <div className="flex items-center justify-center mb-8 gap-0">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.label}>
              <div className="flex flex-col items-center">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold border-2 transition-all shadow-sm"
                  style={{
                    backgroundColor: i < stepIdx ? NAVY : i === stepIdx ? NAVY : "transparent",
                    borderColor: i <= stepIdx ? NAVY : "#CBD5E1",
                    color: i <= stepIdx ? "#fff" : "#94A3B8",
                  }}
                >
                  {i < stepIdx ? <CheckCircle className="w-5 h-5" /> : i + 1}
                </div>
                <span
                  className="text-sm mt-2 font-semibold tracking-wide"
                  style={{ color: i === stepIdx ? NAVY : "#94A3B8" }}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className="h-[3px] w-12 md:w-20 mx-1 mb-7 transition-all"
                  style={{ backgroundColor: i < stepIdx ? NAVY : "#E2E8F0" }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Back button */}
        {BACK_MAP[screen] && (
          <button
            onClick={goBack}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#12345A] mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}

        {errorPopup && (
          <Popup
            type="error"
            message={errorPopup}
            onClose={() => setErrorPopup("")}
          />
        )}

        <div className="bg-white border border-slate-200 rounded-2xl shadow-md p-6 sm:p-8">

        {/* ══════════════════════════════════════════════════════════
            SCREEN: mobile_entry
           ══════════════════════════════════════════════════════════ */}
        {screen === "mobile_entry" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <SectionTitle title="PPI Wallet Transfer" subtitle="Enter the remitter's mobile number to start" icon={Wallet} />
            <div className="mb-6">
              <FieldLabel text="Mobile Number" req />
              <BottomInput
                type="tel" maxLength={10} placeholder="Enter 10-digit mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => e.key === "Enter" && handleGenerateOtp()}
              />
            </div>
            <div className="mt-10">
              <PrimaryBtn onClick={handleGenerateOtp} disabled={loading}>
                {loading ? "Sending OTP…" : "Send OTP"}
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: otp_verify
           ══════════════════════════════════════════════════════════ */}
        {screen === "otp_verify" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <SectionTitle title="Verify OTP" subtitle={`OTP sent to ${mobile}`} icon={Send} />
            <div className="mb-6">
              <FieldLabel text="Enter OTP" req />
              <BottomInput
                type="tel" maxLength={6} placeholder="6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 mb-10">
              <SecondaryBtn onClick={handleGenerateOtp} className="sm:w-1/3">
                Resend OTP
              </SecondaryBtn>
              <PrimaryBtn onClick={handleVerifyOtp} disabled={loading} className="sm:w-2/3">
                {loading ? "Verifying…" : "Verify OTP"}
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: consent
           ══════════════════════════════════════════════════════════ */}
        {screen === "consent" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <SectionTitle title="Consent" subtitle="Select your preferred language and accept" />
            <div className="mb-8">
              <FieldLabel text="Language" req />
              <div className="mt-4 space-y-3">
                {(consentLangs.length > 0
                  ? consentLangs
                  : [{ language_code: "en", language_name: "English" }]
                ).map((l) => (
                  <label
                    key={l.language_code}
                    className="flex items-center gap-4 cursor-pointer py-3 border-b border-gray-100"
                  >
                    <input
                      type="radio" name="lang" value={l.language_code}
                      checked={selectedLang === l.language_code}
                      onChange={() => setSelectedLang(l.language_code)}
                      className="w-5 h-5 accent-[#12345A]"
                    />
                    <span className="text-xl text-gray-800">{l.language_name}</span>
                  </label>
                ))}
              </div>
            </div>
            {consentText && (
              <div className="bg-gray-50 border-l-4 border-[#12345A] p-4 mb-6 max-h-40 overflow-y-auto rounded-r-xl">
                <p className="text-base text-gray-600">{consentText}</p>
              </div>
            )}
            <PrimaryBtn onClick={handleAcceptConsent} disabled={loading}>
              {loading ? "Processing…" : "I Accept & Proceed"}
            </PrimaryBtn>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: ekyc
           ══════════════════════════════════════════════════════════ */}
        {screen === "ekyc" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <SectionTitle title="Aadhaar eKYC" subtitle="Verify remitter's Aadhaar via OTP" icon={Send} />

            <div>
                {!aadhaarOtpSent ? (
                  /* Step 1: enter Aadhaar and generate OTP */
                  <div>
                    <div className="mb-6">
                      <FieldLabel text="Aadhaar Number" req />
                      <BottomInput
                        type="tel" maxLength={12} placeholder="12-digit Aadhaar number"
                        value={aadhaar}
                        onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, "").slice(0, 12))}
                        onKeyDown={(e) => e.key === "Enter" && handleAadhaarOtpGenerate()}
                      />
                    </div>
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <p className="text-sm font-medium text-blue-700">
                        An OTP will be sent to the mobile number registered with Aadhaar.
                      </p>
                    </div>
                    <PrimaryBtn onClick={handleAadhaarOtpGenerate} disabled={loading}>
                      <Send className="w-5 h-5" />
                      {loading ? "Sending OTP…" : "Send Aadhaar OTP"}
                    </PrimaryBtn>
                  </div>
                ) : (
                  /* Step 2: enter OTP and validate */
                  <div>
                    <InfoBox msg={`OTP sent to the Aadhaar-linked mobile for Aadhaar ending in ${aadhaar.slice(-4)}.`} />
                    <div className="mb-6">
                      <FieldLabel text="Enter Aadhaar OTP" req />
                      <BottomInput
                        type="tel" maxLength={6} placeholder="6-digit OTP"
                        value={aadhaarOtp}
                        onChange={(e) => setAadhaarOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        onKeyDown={(e) => e.key === "Enter" && handleAadhaarOtpValidate()}
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 mt-10">
                      <SecondaryBtn onClick={handleAadhaarOtpResend} disabled={loading} className="sm:w-1/3">
                        {loading ? "Resending…" : "Resend OTP"}
                      </SecondaryBtn>
                      <PrimaryBtn onClick={handleAadhaarOtpValidate} disabled={loading} className="sm:w-2/3">
                        {loading ? "Verifying…" : "Verify Aadhaar OTP"}
                      </PrimaryBtn>
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: pan_verify
           ══════════════════════════════════════════════════════════ */}
        {screen === "pan_verify" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <SectionTitle title="PAN Verification" subtitle="Enter the remitter's Permanent Account Number" icon={CreditCard} />
            <div className="mb-6">
              <FieldLabel text="PAN Number" req />
              <BottomInput
                placeholder="e.g. ABCDE1234F" maxLength={10}
                value={pan}
                onChange={(e) => setPan(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && handleVerifyPan()}
              />
            </div>
            <div className="mt-10">
              <PrimaryBtn onClick={handleVerifyPan} disabled={loading}>
                {loading ? "Verifying PAN…" : "Verify PAN"}
              </PrimaryBtn>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: beneficiary_list
           ══════════════════════════════════════════════════════════ */}
        {screen === "beneficiary_list" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-2xl font-semibold text-slate-800">Beneficiaries</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Remitter: <strong>{mobile}{remitterName ? ` · ${remitterName}` : ""}</strong>
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {(availableBalance != null || usedBalance != null || walletBalance != null) && (
                  <div className="flex items-center gap-3 flex-wrap">
                    {availableBalance != null && (
                      <div className="flex flex-col items-center px-4 py-2 rounded-xl bg-green-50 border border-green-200 min-w-[110px]">
                        <span className="text-[10px] font-semibold text-green-600 uppercase tracking-wide">Available</span>
                        <span className="text-base font-bold text-green-700">₹{Number(availableBalance).toFixed(2)}</span>
                      </div>
                    )}
                    {walletBalance != null && (
                      <div className="flex flex-col items-center px-4 py-2 rounded-xl bg-blue-50 border border-blue-200 min-w-[110px]">
                        <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wide">Wallet</span>
                        <span className="text-base font-bold text-[#12345A]">₹{Number(walletBalance).toFixed(2)}</span>
                      </div>
                    )}
                    {usedBalance != null && (
                      <div className="flex flex-col items-center px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 min-w-[110px]">
                        <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wide">Used</span>
                        <span className="text-base font-bold text-amber-700">₹{Number(usedBalance).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => go("add_beneficiary")}
                  className="flex items-center gap-2 px-5 py-3 border-2 border-[#12345A] text-[#12345A] font-bold rounded-xl hover:bg-[#12345A] hover:text-white transition-all text-base"
                >
                  <Plus className="w-4 h-4" /> Add Beneficiary
                </button>
              </div>
            </div>

            {beneficiaries.length === 0 && !loading ? (
              <div className="text-center py-20 text-gray-400">
                <Users className="w-14 h-14 mx-auto mb-3 opacity-30" />
                <p className="text-xl">No beneficiaries added yet.</p>
                <p className="text-base mt-1 text-gray-400">Click "Add Beneficiary" to get started.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {beneficiaries.map((b) => (
                  <div key={b.bene_id}>
                    <div
                      onClick={() => setSelectedBene(selectedBene?.bene_id === b.bene_id ? null : b)}
                      className={`py-4 px-4 rounded-xl border cursor-pointer transition-all flex items-start justify-between ${
                        selectedBene?.bene_id === b.bene_id ? "bg-[#12345A]/5 border-[#12345A]/20 shadow-sm" : "border-slate-100 bg-slate-50/60 hover:border-slate-200 hover:shadow-sm"
                      }`}
                    >
                      <div className="px-2 flex-1">
                        <p className="text-[22px] font-bold text-slate-800">{b.bene_name}</p>
                        <p className="text-base text-gray-500 mt-0.5">{b.bene_bank}</p>
                        <p className="text-sm font-mono text-gray-500 mt-1">{b.bene_acc_no} · {b.bene_ifsc}</p>
                      </div>
                      <div className="flex items-center gap-3 px-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${b.isAcValidate === 1 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {b.isAcValidate === 1 ? "Validated" : "Pending"}
                        </span>
                        <button
                          onClick={(e) => handleDeleteBene(b.bene_id, b.bene_acc_no, e)}
                          className="p-2 text-red-400 hover:text-red-600"
                          title="Delete beneficiary"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Inline delete OTP row */}
                    {deleteBeneOtpTarget?.bene_id === b.bene_id && (
                      <div className="bg-red-50 border border-red-200 rounded-b-xl px-4 py-4 flex flex-col gap-3 animate-[fadeIn_.2s_ease] -mt-1">
                        <p className="text-sm font-semibold text-red-700">Enter OTP sent to {mobile} to confirm deletion</p>
                        <div className="flex flex-col sm:flex-row gap-3 items-center">
                          <input
                            type="tel" maxLength={6} placeholder="6-digit OTP"
                            value={deleteBeneOtp}
                            onChange={(e) => setDeleteBeneOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 min-h-[52px] bg-[#F6F6F6] border border-red-400 rounded-xl px-4 py-2.5 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-red-100 transition"
                          />
                          <div className="flex gap-2 shrink-0">
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteBeneOtpTarget(null); setDeleteBeneOtp(""); }}
                              className="px-4 py-2.5 rounded-xl border-2 border-gray-200 text-gray-500 font-bold text-sm hover:bg-gray-50 transition"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteBeneOtpValidate(); }}
                              disabled={loading}
                              className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-xl transition disabled:opacity-60"
                            >
                              {loading ? "Deleting…" : "Confirm Delete"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Inline transfer row */}
                    {selectedBene?.bene_id === b.bene_id && (
                      <div className="bg-[#12345A]/5 border border-t-0 border-[#12345A]/15 rounded-b-xl px-4 py-4 flex flex-col gap-3 animate-[fadeIn_.2s_ease] -mt-1">
                        <div className="flex flex-col sm:flex-row gap-4 items-center">
                          <div className="flex items-center gap-2 flex-1 w-full">
                            <span className="text-2xl text-gray-500 font-bold">₹</span>
                            <input
                              type="number" min="1" placeholder="Enter amount"
                              value={transferAmt} onChange={(e) => { setTransferAmt(e.target.value); setFeeData(null); }}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-h-[52px] px-3 py-2 rounded-xl border border-[#12345A] bg-[#F6F6F6] text-slate-800 text-xl font-bold focus:outline-none focus:ring-2 focus:ring-indigo-100"
                            />
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (transferAmt && parseFloat(transferAmt) > 0) {
                                setOtpSent(false); setMpin(""); setTxnOtp(["","","","","",""]); setTxnOtpToken(""); go("fund_transfer");
                              } else { setErrorPopup("Enter a valid amount"); }
                            }}
                            className="flex items-center gap-2 px-6 py-3 bg-[#12345A] text-white font-bold rounded-xl text-base hover:bg-[#0d2644] transition-all shrink-0"
                          >
                            <Send className="w-4 h-4" /> Proceed to Transfer
                          </button>
                        </div>

                        {/* Fee breakdown */}
                        {feeLoading && (
                          <p className="text-xs text-gray-400 font-semibold animate-pulse px-1">Calculating fee…</p>
                        )}
                        {!feeLoading && feeData && (
                          <div className="flex flex-wrap gap-4 text-xs font-semibold px-1 pt-1 border-t border-[#12345A]/10">
                            {feeData.fee        != null && <span className="text-gray-500">Service Fee: <strong className="text-gray-800">₹{Number(feeData.fee).toFixed(2)}</strong></span>}
                            {feeData.net_amount  != null && <span className="text-gray-500">Net Amount: <strong className="text-gray-800">₹{Number(feeData.net_amount).toFixed(2)}</strong></span>}
                            {feeData.txn_amount  != null && <span className="text-[#12345A]">Total Debit: <strong>₹{Number(feeData.txn_amount).toFixed(2)}</strong></span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: add_beneficiary
           ══════════════════════════════════════════════════════════ */}
        {screen === "add_beneficiary" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* ── Left: form ─────────────────────────────────────── */}
              <div className="flex-1 min-w-0">
            <SectionTitle
              title="Add Beneficiary"
              subtitle={addBeneOtpSent ? `Verify OTP for account ${newBene.accountNumber}` : "Fill in the beneficiary's bank details"}
              icon={Users}
            />

            {/* ── OTP verification step ─────────────────────────────── */}
            {addBeneOtpSent ? (
              <div>
                <InfoBox msg={`An OTP has been sent to verify the beneficiary account ${newBene.accountNumber}. Enter it below.`} />
                <div className="mb-6">
                  <FieldLabel text="Beneficiary OTP" req />
                  <BottomInput
                    type="tel" maxLength={6} placeholder="6-digit OTP"
                    value={addBeneOtp}
                    onChange={(e) => setAddBeneOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onKeyDown={(e) => e.key === "Enter" && handleBeneOtpValidate()}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mt-10 mb-10">
                  <SecondaryBtn onClick={handleBeneOtpResend} disabled={loading} className="sm:w-1/3">
                    {loading ? "Resending…" : "Resend OTP"}
                  </SecondaryBtn>
                  <PrimaryBtn onClick={handleBeneOtpValidate} disabled={loading} className="sm:w-2/3">
                    {loading ? "Verifying…" : "Verify & Activate Beneficiary"}
                  </PrimaryBtn>
                </div>
              </div>
            ) : (
              <div>{/* ── Beneficiary form ──────────────────────────── */}

            {/* Beneficiary Full Name */}
            <div className="mb-6">
              <FieldLabel text="Beneficiary Full Name" req />
              <BottomInput
                type="text" placeholder="Full name as per bank records"
                value={newBene.beneficiaryName}
                onChange={(e) => setNewBene((p) => ({ ...p, beneficiaryName: e.target.value }))}
              />
            </div>

            {/* Beneficiary Mobile */}
            <div className="mb-6">
              <FieldLabel text="Beneficiary Mobile" />
              <BottomInput
                type="tel" maxLength={10} placeholder="10-digit mobile number"
                value={newBene.beneficiaryPhoneNumber}
                onChange={(e) => setNewBene((p) => ({ ...p, beneficiaryPhoneNumber: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
              />
            </div>

            {/* Bank Search */}
            <div className="mb-6 relative" ref={bankDropdownRef}>
              <FieldLabel text="Bank Name" req />
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search bank name…"
                  value={bankSearch}
                  onChange={(e) => { setBankSearch(e.target.value); setBankDropdownOpen(true); }}
                  onFocus={() => setBankDropdownOpen(true)}
                  className="w-full min-h-[60px] sm:min-h-[70px] pl-10 pr-3 py-3 rounded-xl border border-[#12345A] focus:ring-2 focus:ring-indigo-100 focus:border-indigo-100 transition text-xl sm:text-[22px] font-bold bg-[#F6F6F6] shadow-sm placeholder-slate-400"
                />
              </div>
              {bankDropdownOpen && filteredBanks.length > 0 && (
                <div className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-2xl max-h-56 overflow-y-auto mt-1">
                  {filteredBanks.map((b) => (
                    <button
                      key={b.bank_code}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setNewBene((p) => ({ ...p, bankName: b.bank_name, bankIfscCode: b.ifsc_code_sample }));
                        setBankSearch(b.bank_name);
                        setBankDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-[#12345A]/5 border-b border-gray-100 last:border-0 transition-colors"
                    >
                      <p className="text-sm font-semibold text-gray-900">{b.bank_name}</p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{b.ifsc_code_sample}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* IFSC Code – auto-filled, editable */}
            <div className="mb-6">
              <FieldLabel text="IFSC Code" req />
              <BottomInput
                type="text" maxLength={11} placeholder="e.g. HDFC0001234"
                value={newBene.bankIfscCode}
                onChange={(e) => setNewBene((p) => ({ ...p, bankIfscCode: e.target.value.toUpperCase() }))}
              />
              {newBene.bankIfscCode && (
                <p className="text-xs text-gray-400 mt-1 px-1">Auto-filled from selected bank — edit branch IFSC if needed</p>
              )}
            </div>

            {/* Account Number */}
            <div className="mb-6">
              <FieldLabel text="Account Number" req />
              <BottomInput
                type="text" maxLength={20} placeholder="Bank account number"
                value={newBene.accountNumber}
                onChange={(e) => setNewBene((p) => ({ ...p, accountNumber: e.target.value }))}
              />
            </div>

            {/* MPIN */}
            <div className="mb-6">
              <FieldLabel text="Your MPIN" req />
              <BottomInput
                type="password" maxLength={6} placeholder="Your merchant MPIN"
                value={newBene.mpin}
                onChange={(e) => setNewBene((p) => ({ ...p, mpin: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-10 mb-10">
              <SecondaryBtn onClick={() => { setAddBeneOtpSent(false); setAddBeneOtp(""); go("beneficiary_list"); }} className="sm:w-1/3">
                Cancel
              </SecondaryBtn>
              <PrimaryBtn onClick={handleAddBene} disabled={loading} className="sm:w-2/3">
                {loading ? "Adding Beneficiary…" : "Add Beneficiary"}
              </PrimaryBtn>
            </div>
            </div>
            )}
              </div>{/* end left form */}

              {/* ── Right: wallet balance card ──────────────────────── */}
              {(availableBalance != null || usedBalance != null || walletBalance != null) && (
                <div className="lg:w-64 shrink-0">
                  <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 sticky top-4">
                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Wallet Summary</p>
                    {availableBalance != null && (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-gray-400">Available Balance</span>
                        <span className="text-xl font-bold text-green-600">₹{Number(availableBalance).toFixed(2)}</span>
                      </div>
                    )}
                    {walletBalance != null && (
                      <div className="flex flex-col gap-0.5 border-t pt-3">
                        <span className="text-xs text-gray-400">Wallet Balance</span>
                        <span className="text-lg font-semibold text-[#12345A]">₹{Number(walletBalance).toFixed(2)}</span>
                      </div>
                    )}
                    {usedBalance != null && (
                      <div className="flex flex-col gap-0.5 border-t pt-3">
                        <span className="text-xs text-gray-400">Used Balance</span>
                        <span className="text-lg font-semibold text-amber-600">₹{Number(usedBalance).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>{/* end flex row */}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: fund_transfer
           ══════════════════════════════════════════════════════════ */}
        {screen === "fund_transfer" && selectedBene && (
          <div className="animate-[fadeIn_.3s_ease]">
            <SectionTitle title="Confirm Transfer" subtitle="Review details before proceeding" icon={Send} />

            {/* Payment Mode selector */}
            <div className="mb-6">
              <FieldLabel text="Payment Mode" req />
              <div className="flex gap-3 mt-2">
                {["IMPS", "NEFT"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setPaymentMode(mode)}
                    className={`flex-1 py-3 rounded-xl border-2 font-bold text-base transition-all ${
                      paymentMode === mode
                        ? "border-[#12345A] bg-[#12345A] text-white shadow"
                        : "border-gray-200 text-gray-500 hover:border-[#12345A] hover:text-[#12345A]"
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary table */}
            <div className="mb-8 bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
              {[
                ["To", selectedBene.bene_name],
                ["Bank", selectedBene.bene_bank],
                ["Account", selectedBene.bene_acc_no],
                ["IFSC", selectedBene.bene_ifsc],
                ["Amount", `₹${parseFloat(transferAmt).toFixed(2)}`],
                feeData?.fee        != null && ["Service Fee", `₹${Number(feeData.fee).toFixed(2)}`],
                feeData?.net_amount  != null && ["Net Amount", `₹${Number(feeData.net_amount).toFixed(2)}`],
                feeData?.txn_amount  != null && ["Total Debit", `₹${Number(feeData.txn_amount).toFixed(2)}`],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} className={`flex items-center justify-between px-4 py-3.5 ${label === "Total Debit" ? "bg-[#12345A]/5" : ""}`}>
                  <span className="text-base font-medium text-slate-500">{label}</span>
                  <span className={`font-bold ${label === "Total Debit" ? "text-[#12345A] text-xl" : "text-slate-800 text-lg"}`}>{value}</span>
                </div>
              ))}
            </div>

            {/* MPIN */}
            <div className="mb-6">
              <FieldLabel text="Your MPIN" req />
              <BottomInput
                type="password" maxLength={6} placeholder="Enter your MPIN"
                value={mpin}
                onChange={(e) => setMpin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
            </div>

            {!otpSent ? (
              <PrimaryBtn onClick={handleSendTxnOtp} disabled={loading}>
                {loading ? "Sending OTP…" : "Send OTP to Remitter"}
              </PrimaryBtn>
            ) : (
              <div>
                <InfoBox msg={`OTP sent to remitter's mobile ${mobile}. Enter it below to complete the transfer.`} />
                <div className="mb-6">
                  <FieldLabel text="Transaction OTP" req />
                  <div className="flex gap-2 mt-4 justify-center">
                    {txnOtp.map((digit, i) => (
                      <input
                        key={i} ref={(el) => (txnOtpRefs.current[i] = el)}
                        type="text" maxLength={1} value={digit}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, "");
                          const arr = [...txnOtp]; arr[i] = v; setTxnOtp(arr);
                          if (v && i < 5) txnOtpRefs.current[i + 1]?.focus();
                        }}
                        onKeyDown={(e) => { if (e.key === "Backspace" && !txnOtp[i] && i > 0) txnOtpRefs.current[i - 1]?.focus(); }}
                        className="w-12 h-16 sm:w-14 sm:h-[70px] text-center text-2xl font-bold border border-[#12345A] rounded-xl bg-[#F6F6F6] focus:ring-2 focus:ring-indigo-100 outline-none transition shadow-sm"
                      />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mt-10 mb-10">
                  <SecondaryBtn onClick={() => setOtpSent(false)} className="sm:w-1/3">
                    Resend OTP
                  </SecondaryBtn>
                  <PrimaryBtn onClick={handleConfirmTransfer} disabled={loading} className="sm:w-2/3">
                    {loading ? "Processing…" : "Confirm Transfer"}
                  </PrimaryBtn>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: tx_status
           ══════════════════════════════════════════════════════════ */}
        {screen === "tx_status" && txnResult && (
          <div className="animate-[fadeIn_.3s_ease] text-center py-8">
            {/* Status icon */}
            <div
              className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6"
              style={{
                backgroundColor:
                  txnResult.status === "SUCCESS" ? "#f0fdf4"
                  : txnResult.status === "PENDING" ? "#fffbeb"
                  : "#fef2f2",
              }}
            >
              {txnResult.status === "SUCCESS" ? (
                <CheckCircle className="w-12 h-12 text-green-600" />
              ) : txnResult.status === "PENDING" ? (
                <Clock className="w-12 h-12 text-amber-500" />
              ) : (
                <AlertCircle className="w-12 h-12 text-red-500" />
              )}
            </div>

            <h2
              className="text-4xl font-bold mb-2"
              style={{
                color:
                  txnResult.status === "SUCCESS" ? "#16a34a"
                  : txnResult.status === "PENDING" ? "#d97706"
                  : "#dc2626",
              }}
            >
              {txnResult.status}
            </h2>
            {txnResult.customerSummary && (
              <p className="text-base text-gray-500 mb-6">{txnResult.customerSummary}</p>
            )}

            {/* Details */}
            <div className="text-left mt-6 bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
              {[
                txnResult.txnId && ["Transaction ID", txnResult.txnId],
                txnResult.externalRefNumber && ["Reference No", txnResult.externalRefNumber],
                ["Amount", `₹${parseFloat(transferAmt).toFixed(2)}`],
                ["Beneficiary", selectedBene?.bene_name],
                ["Time", new Date().toLocaleString()],
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} className="flex items-center justify-between px-4 py-3.5">
                  <span className="text-base font-medium text-slate-500">{label}</span>
                  <span className="text-base font-bold text-slate-800 font-mono">{value}</span>
                </div>
              ))}
            </div>

            {txnResult.status === "PENDING" && (
              <button
                onClick={handleCheckStatus} disabled={loading}
                className="flex items-center gap-2 mx-auto mt-4 mb-2 text-base font-semibold text-[#12345A] hover:underline"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Check Status
              </button>
            )}

            <div className="flex flex-col sm:flex-row gap-4 mt-10 mb-10">
              <SecondaryBtn
                onClick={() => { setSelectedBene(null); setTransferAmt(""); loadBeneficiaries(); go("beneficiary_list"); }}
                className="sm:w-1/2"
              >
                New Transfer
              </SecondaryBtn>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════
            SCREEN: refund
           ══════════════════════════════════════════════════════════ */}
        {screen === "refund" && (
          <div className="animate-[fadeIn_.3s_ease]">
            <SectionTitle
              title="Refund"
              subtitle={`Refund transaction ${txnResult?.externalRefNumber || txnResult?.txnId}`}
              icon={RefreshCw}
            />

            {refundResult ? (
              <div className="text-center py-8">
                <div className="w-24 h-24 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-6">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-3xl font-bold text-green-700 mb-4">Refund Successful</h3>
                {refundResult.refundTxnId && (
                  <p className="text-base text-gray-600 mb-2">Refund ID: <strong>{refundResult.refundTxnId}</strong></p>
                )}
                {refundResult.refundtxnreferenceid && (
                  <p className="text-base text-gray-500 mb-8">Ref: {refundResult.refundtxnreferenceid}</p>
                )}
                <PrimaryBtn onClick={() => { setSelectedBene(null); setTransferAmt(""); loadBeneficiaries(); go("beneficiary_list"); }}>
                  Done
                </PrimaryBtn>
              </div>
            ) : !refundOtpToken ? (
              <PrimaryBtn onClick={handleRefundSendOtp} disabled={loading}>
                {loading ? "Sending…" : "Send Refund OTP"}
              </PrimaryBtn>
            ) : (
              <div>
                <InfoBox msg={`Refund OTP sent to ${mobile}.`} />
                <div className="mb-6">
                  <FieldLabel text="Refund OTP" req />
                  <BottomInput
                    type="tel" maxLength={6} placeholder="Enter refund OTP"
                    value={refundOtp}
                    onChange={(e) => setRefundOtp(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-4 mt-10 mb-10">
                  <SecondaryBtn onClick={() => setRefundOtpToken("")} className="sm:w-1/3">
                    Resend
                  </SecondaryBtn>
                  <PrimaryBtn onClick={handleRefundValidate} disabled={loading} className="sm:w-2/3">
                    {loading ? "Processing…" : "Complete Refund"}
                  </PrimaryBtn>
                </div>
              </div>
            )}
          </div>
        )}

        </div>
      </div>
    </div>
  );
}
